import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { ToastService } from './toast.service';
import type { ToastType } from './toast.service';

@Component({
  selector: 'app-toast',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="fixed bottom-4 right-4 z-50 flex flex-col gap-2" role="alert" aria-live="polite">
      @for (toast of toastService.toasts(); track toast.id) {
        <div
          [attr.data-testid]="'toast-' + toast.type"
          class="flex items-start gap-3 px-4 py-3 rounded-lg border text-sm min-w-72 max-w-md shadow-lg animate-[slide-in-right_200ms_ease-out]"
          [class]="typeClasses(toast.type)"
        >
          <!-- Icon -->
          <svg
            class="h-5 w-5 shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            @switch (toast.type) {
              @case ('success') {
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              }
              @case ('error') {
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              }
              @case ('warning') {
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              }
              @case ('info') {
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              }
            }
          </svg>
          <!-- Message -->
          <span class="flex-1">{{ toast.message }}</span>
          <!-- Dismiss -->
          <button
            aria-label="Dismiss notification"
            class="shrink-0 opacity-60 hover:opacity-100 transition-opacity"
            (click)="toastService.dismiss(toast.id)"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      }
    </div>
  `,
})
export class ToastComponent {
  readonly toastService = inject(ToastService);

  typeClasses(type: ToastType): string {
    switch (type) {
      case 'success':
        return 'bg-success-50 text-success-600 border-success-500/20';
      case 'error':
        return 'bg-error-50 text-error-600 border-error-500/20';
      case 'warning':
        return 'bg-warning-50 text-warning-600 border-warning-500/20';
      case 'info':
        return 'bg-primary-50 text-primary-600 border-primary-500/20';
    }
  }
}
